import {$host} from './index';
import {readPublicSearchPacket} from '../utils/publicSearchResponse';
import {variantIdsFromHits, buildLegacySearchResponse, buildLegacyTips} from './smartSearchAdapter';

const headers = {Accept: 'application/vnd.you-wanna.public-search-v1+json'};
const unavailable = (status = 503) => {
    const error = new Error('Не удалось загрузить результаты поиска');
    error.status = status;
    return error;
};
const own = (value, key) => {
    if (value === null || (typeof value !== 'object' && typeof value !== 'function')) return undefined;
    const descriptor = Object.getOwnPropertyDescriptor(value, key);
    return descriptor && Object.prototype.hasOwnProperty.call(descriptor, 'value') ? descriptor.value : undefined;
};
const queryValid = query => typeof query === 'string' && query.trim().length > 0 && query.trim().length <= 120;
const samePair = (before, after) => !after.handled && before.publicationMode === after.publicationMode &&
    before.commerceMode === after.commerceMode;
const rowsResult = (packet, limit, offset) => ({...packet.payload, recognized: [],
    next_offset: offset + limit < packet.payload.count ? offset + limit : null});

async function currentPacket(operation, input) {
    let response;
    try {
        response = operation === 'rows' ? await $host.get('/api/catalog/search-product', {params: input, headers})
            : await $host.post(operation === 'tips' ? '/api/catalog/search-tips' : '/api/catalog/fetchproductsbyid', input, {headers});
    } catch (error) {
        // Axios has already finished its transport processing. Do not consult a
        // raw error message, coercible status or caller-owned response getter.
        let status = 503;
        try {status = readPublicSearchPacket(operation, own(error, 'response')).status;} catch { /* bounded refusal */ }
        throw unavailable([400, 404, 503].includes(status) ? status : 503);
    }
    const result = readPublicSearchPacket(operation, response);
    if (!result.ok) throw unavailable(result.status);
    return result.packet;
}

async function finalPacket(first, operation, input) {
    const last = await currentPacket(operation, input);
    if (!samePair(first, last)) throw unavailable();
    return last;
}

export async function productSearchBound(q, limit, offset = 0) {
    if (!queryValid(q) || !Number.isSafeInteger(limit) || limit <= 0 || limit > 100 ||
        !Number.isSafeInteger(offset) || offset < 0 || offset > 100000) throw unavailable(400);
    const input = {q, limit, offset};
    const first = await currentPacket('rows', input);
    if (first.handled) return rowsResult(first, limit, offset);

    let smart;
    try {
        const response = await $host.get('/smart-search-api/search-raw', {params: input});
        smart = response.data;
    } catch {
        const last = await finalPacket(first, 'rows', input);
        return {...rowsResult(last, limit, offset), smart_fallback: true};
    }
    let variantIds;
    try {variantIds = variantIdsFromHits(smart.hits);} catch {throw unavailable();}
    let hydratedProducts = [];
    if (variantIds.length) {
        const hydration = await currentPacket('ids', {arrayId: variantIds});
        if (!samePair(first, hydration) || hydration.payload.data.length === 0) throw unavailable();
        hydratedProducts = hydration.payload.data;
    }
    const hydratedVariantIds = new Set(hydratedProducts.flatMap(product => [product && product.id,
        ...((product && product.product && product.product.childProducts) || []).map(child => child && child.id)])
        .map(id => Number(id)).filter(id => Number.isInteger(id) && id > 0));
    if (!variantIds.every(id => hydratedVariantIds.has(id))) throw unavailable();
    let snapshotProducts = [];
    if (variantIds.length) {
        try {
            const {data: snapshotResponse} = await $host.get('/smart-search-api/cards', {params: {ids: variantIds.join(',')}});
            snapshotProducts = Array.isArray(snapshotResponse && snapshotResponse.data) ? snapshotResponse.data.map(product => {
                const productData = product && product.product;
                return {...product, smart_test_only: !hydratedVariantIds.has(Number(product && product.id)),
                    product: productData ? {...productData, childProducts: (productData.childProducts || []).map(child => ({
                        ...child, smart_test_only: !hydratedVariantIds.has(Number(child && child.id)),
                    }))} : productData};
            }) : [];
        } catch {snapshotProducts = [];}
    }
    // A current packet is mandatory even after a successful upstream response.
    // Its errors/transitions are final, never caught by the transport fallback.
    await finalPacket(first, 'rows', input);
    try {return buildLegacySearchResponse(smart, hydratedProducts.concat(snapshotProducts));}
    catch {throw unavailable();}
}

export async function searchTipsBound(query) {
    if (!queryValid(query)) throw unavailable(400);
    const input = {query};
    const first = await currentPacket('tips', input);
    if (first.handled) return first.payload;
    let smart;
    try {
        const response = await $host.get('/smart-search-api/tips', {params: {q: query, limit: 6}});
        smart = response.data;
    } catch {
        return (await finalPacket(first, 'tips', input)).payload;
    }
    await finalPacket(first, 'tips', input);
    try {return buildLegacyTips(smart);} catch {throw unavailable();}
}
